import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  Person,
  Email,
  Phone,
  LocationOn,
  Business,
  CalendarToday,
  Verified,
  Star,
  TrendingUp,
  Group,
  AttachMoney,
  Assessment,
  Settings,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { getPartnerProfile, getPartnerContributions, getMothersByPartner } from '../api';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

const PartnerProfile = () => {
  const { partnerId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [partnerData, setPartnerData] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [mothers, setMothers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});


  useEffect(() => {
    const fetchPartnerData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [profile, contributionsData, mothersData] = await Promise.all([
          getPartnerProfile(partnerId),
          getPartnerContributions(partnerId),
          getMothersByPartner(partnerId),
        ]);

        setPartnerData(profile);
        setContributions(Array.isArray(contributionsData) ? contributionsData : []);
        setMothers(mothersData.mothers || []);
        setEditForm({
          name: profile.name || '',
          email: profile.email || '',
          phone_number: profile.phone_number || '',
          location: profile.location || '',
          organization: profile.organization || '',
          bio: profile.bio || '',
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPartnerData();
  }, [partnerId]);

  const handleEdit = () => {
    setEditing(true);
    setError(null);
  };

  const handleCancel = () => {
    setEditing(false);
    setEditForm({
      name: partnerData.name || '',
      email: partnerData.email || '',
      phone_number: partnerData.phone_number || '',
      location: partnerData.location || '',
      organization: partnerData.organization || '',
      bio: partnerData.bio || '',
    });
  };

  const handleSave = async () => {
    try {
      // Here you would typically call an API to update the partner profile
      // For now, we'll just update the local state
      setPartnerData({ ...partnerData, ...editForm });
      setEditing(false);
      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(err.message);
    }
  };

  // Calculate statistics
  const totalContributions = contributions.length;
  const totalAmount = contributions.reduce((sum, c) => sum + c.amount, 0);
  const totalMothers = mothers.length;
  const averageContribution = totalContributions > 0 ? Math.round(totalAmount / totalContributions) : 0;
  
  const StatCard = ({ title, value, icon, color, subtitle, description }) => (
    <Card sx={{ 
      borderRadius: 3, 
      boxShadow: '0 8px 32px rgba(249, 41, 85, 0.12)',
      background: color,
      color: 'white',
      position: 'relative',
      overflow: 'hidden',
      flex: '1 1 250px',
      minWidth: '250px',
      maxWidth: '100%',
    }}>
      <Box sx={{
        position: 'absolute',
        top: -20,
        right: -20,
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.1)',
      }} />
      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
            {value}
          </Typography>
          <Box sx={{ color: 'white' }}>
            {icon}
          </Box>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        {description && (
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ 
      p: isMobile ? 2 : 4, 
              bgcolor: '#FFFFFF',
        minHeight: '100vh',
        background: '#FFFFFF'
    }}>
      

      {loading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          my: 8 
        }}>
          <CircularProgress size={60} sx={{ color: '#f92955' }} />
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {!loading && !error && partnerData && (
        <>
          {/* Profile Header */}
          <Paper sx={{ 
            p: 4, 
            mb: 4, 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f0f0f0',
            background: '#FFFFFF',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #f92955, #d41e3f)',
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexDirection: isMobile ? 'column' : 'row' }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  sx={{
                    width: isMobile ? 100 : 140,
                    height: isMobile ? 100 : 140,
                    bgcolor: '#f92955',
                    fontSize: isMobile ? '2.5rem' : '3.5rem',
                    fontWeight: 700,
                    border: '4px solid #fff',
                    boxShadow: '0 4px 20px rgba(249, 41, 85, 0.3)',
                  }}
                >
                  {partnerData.name ? partnerData.name.charAt(0).toUpperCase() : 'P'}
                </Avatar>
                <Chip
                  label="Verified Partner"
                  icon={<Verified sx={{ fontSize: 16 }} />}
                  sx={{
                    position: 'absolute',
                    bottom: -15,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: '#f92955',
                    color: '#FFFFFF',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 28,
                    boxShadow: '0 4px 12px rgba(249, 41, 85, 0.3)',
                    '& .MuiChip-icon': {
                      color: '#fff',
                    }
                  }}
                />
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Typography variant="h3" sx={{ 
                    color: '#000000', 
                    fontWeight: 700,
                    fontSize: isMobile ? '1.8rem' : '2.5rem',
                  }}>
                    {partnerData.name || 'Partner Name'}
                  </Typography>
                  <Chip
                    label={`ID: ${partnerId}`}
                    sx={{
                                          bgcolor: '#FCE4EC',
                    color: '#f92955',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      border: '1px solid #ff69b430',
                      height: 32,
                    }}
                  />
                  <Box sx={{ ml: 'auto' }}>
                    <Button
                      variant={editing ? "outlined" : "contained"}
                      startIcon={editing ? <Cancel /> : <Edit />}
                      onClick={editing ? handleCancel : handleEdit}
                      sx={{
                        bgcolor: editing ? 'transparent' : '#f92955',
                        color: editing ? '#f92955' : '#fff',
                        borderColor: '#f92955',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                        '&:hover': {
                          bgcolor: editing ? '#f9295515' : '#d41e3f',
                        },
                      }}
                    >
                      {editing ? 'Cancel' : 'Edit Profile'}
                    </Button>
                  </Box>
                </Box>
                
                <Typography variant="h6" sx={{ 
                  color: '#666', 
                  mb: 3, 
                  lineHeight: 1.6,
                  fontSize: '1rem',
                  maxWidth: '600px'
                }}>
                  {partnerData.bio || 'Dedicated partner committed to empowering mothers and communities through sustainable development initiatives.'}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<Business />}
                    label={partnerData.organization || 'Organization'}
                    variant="outlined"
                    sx={{ 
                      borderColor: '#f92955', 
                      color: '#f92955',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      height: 36,
                    }}
                  />
                  <Chip
                    icon={<LocationOn />}
                    label={partnerData.location || 'Location'}
                    variant="outlined"
                    sx={{ 
                      borderColor: '#BBFFC1', 
                      color: '#BBFFC1',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      height: 36,
                    }}
                  />
                  <Chip
                    icon={<Star />}
                    label="Premium Partner"
                    sx={{ 
                      bgcolor: '#f92955', 
                      color: '#fff', 
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      height: 36,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Statistics Cards */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row', 
            gap: 2, 
            mb: 4, 
            width: '100%',
            flexWrap: 'wrap'
          }}>
            <StatCard
              title="Total Contributions"
              value={totalContributions}
              icon={<AttachMoney sx={{ fontSize: 40, opacity: 0.8 }} />}
              color="linear-gradient(135deg, #f92955 0%, #d41e3f 100%)"
              description="All time contributions"
            />
            <StatCard
              title="Total Amount"
              value={`UGX ${totalAmount.toLocaleString()}`}
              icon={<TrendingUp sx={{ fontSize: 40, opacity: 0.8 }} />}
              color="linear-gradient(135deg, #BBFFC1 0%, #a8ffae 100%)"
              description="Total funds contributed"
            />
            <StatCard
              title="Mothers Supported"
              value={totalMothers}
              icon={<Group sx={{ fontSize: 40, opacity: 0.8 }} />}
              color="linear-gradient(135deg, #f92955 0%, #d41e3f 100%)"
              description="Active mothers"
            />
            <StatCard
              title="Avg Contribution"
              value={`UGX ${averageContribution.toLocaleString()}`}
              icon={<Assessment sx={{ fontSize: 40, opacity: 0.8 }} />}
              color="linear-gradient(135deg, #BBFFC1 0%, #a8ffae 100%)"
              description="Per contribution"
            />
          </Box>

          {/* Profile Details */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper sx={{ 
                p: 4, 
                borderRadius: 3, 
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0',
                background: '#FFFFFF',
                color: '#333'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                  <Settings sx={{ mr: 2, color: '#f92955', fontSize: 24 }} />
                  <Typography variant="h5" sx={{ color: '#333', fontWeight: 600 }}>
                    Profile Information
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Full Name"
                      value={editing ? editForm.name : partnerData.name}
                      onChange={(e) => editing && setEditForm({ ...editForm, name: e.target.value })}
                      disabled={!editing}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        startAdornment: <Person sx={{ mr: 1, color: '#999' }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: editing ? '#f92955' : '#e0e0e0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#f92955',
                          },
                          '&.Mui-disabled': {
                            bgcolor: '#f8f9fa',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: editing ? '#f92955' : '#666',
                          fontWeight: 600,
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email Address"
                      value={editing ? editForm.email : partnerData.email}
                      onChange={(e) => editing && setEditForm({ ...editForm, email: e.target.value })}
                      disabled={!editing}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        startAdornment: <Email sx={{ mr: 1, color: '#999' }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: editing ? '#f92955' : '#e0e0e0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#f92955',
                          },
                          '&.Mui-disabled': {
                            bgcolor: '#f8f9fa',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: editing ? '#f92955' : '#666',
                          fontWeight: 600,
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone Number"
                      value={editing ? editForm.phone_number : partnerData.phone_number}
                      onChange={(e) => editing && setEditForm({ ...editForm, phone_number: e.target.value })}
                      disabled={!editing}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        startAdornment: <Phone sx={{ mr: 1, color: '#999' }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: editing ? '#f92955' : '#e0e0e0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#f92955',
                          },
                          '&.Mui-disabled': {
                            bgcolor: '#f8f9fa',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: editing ? '#f92955' : '#666',
                          fontWeight: 600,
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Location"
                      value={editing ? editForm.location : partnerData.location}
                      onChange={(e) => editing && setEditForm({ ...editForm, location: e.target.value })}
                      disabled={!editing}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        startAdornment: <LocationOn sx={{ mr: 1, color: '#999' }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: editing ? '#f92955' : '#e0e0e0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#f92955',
                          },
                          '&.Mui-disabled': {
                            bgcolor: '#f8f9fa',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: editing ? '#f92955' : '#666',
                          fontWeight: 600,
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      label="Organization"
                      value={editing ? editForm.organization : partnerData.organization}
                      onChange={(e) => editing && setEditForm({ ...editForm, organization: e.target.value })}
                      disabled={!editing}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        startAdornment: <Business sx={{ mr: 1, color: '#999' }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: editing ? '#f92955' : '#e0e0e0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#f92955',
                          },
                          '&.Mui-disabled': {
                            bgcolor: '#f8f9fa',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: editing ? '#f92955' : '#666',
                          fontWeight: 600,
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      label="Bio"
                      value={editing ? editForm.bio : partnerData.bio}
                      onChange={(e) => editing && setEditForm({ ...editForm, bio: e.target.value })}
                      disabled={!editing}
                      fullWidth
                      multiline
                      rows={4}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: editing ? '#f92955' : '#e0e0e0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#f92955',
                          },
                          '&.Mui-disabled': {
                            bgcolor: '#f8f9fa',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: editing ? '#f92955' : '#666',
                          fontWeight: 600,
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                {editing && (
                  <Box sx={{ display: 'flex', gap: 2, mt: 4, pt: 3, borderTop: '1px solid #f0f0f0' }}>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={handleSave}
                      sx={{
                        bgcolor: '#4caf50',
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
                        '&:hover': {
                          bgcolor: '#45a049',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Save Changes
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleCancel}
                      sx={{
                        borderColor: '#f92955',
                        color: '#f92955',
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        '&:hover': {
                          borderColor: '#d41e3f',
                          bgcolor: '#f9295515',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ 
                p: 4, 
                borderRadius: 3, 
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0',
                background: '#FFFFFF',
                color: '#333'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                  <CalendarToday sx={{ mr: 2, color: '#f92955', fontSize: 24 }} />
                  <Typography variant="h5" sx={{ color: '#333', fontWeight: 600 }}>
                    Partner Details
                  </Typography>
                </Box>

                <Box sx={{ space: 2 }}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>
                      Partner ID
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#333', fontWeight: 600, fontFamily: 'monospace' }}>
                      {partnerId}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>
                      Member Since
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#333', fontWeight: 600 }}>
                      {partnerData.created_at ? new Date(partnerData.created_at).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>
                      Status
                    </Typography>
                    <Chip
                      label="Active"
                      sx={{
                        bgcolor: '#BBFFC1',
                        color: '#000',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Box>
                    <Typography variant="body2" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>
                      Partner Level
                    </Typography>
                    <Chip
                      label="Premium"
                      icon={<Star />}
                      sx={{
                        bgcolor: '#f92955',
                        color: '#fff',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default PartnerProfile;
